import { useEffect, useRef } from 'react';
import { useRouter } from '@tanstack/react-router';

import { TabsList, TabsTrigger } from '@/components/ui/tabs';

import { ADMIN_PAGE_SECTION } from '../adminLayout';
import { useAdminWorkspaces } from '../hooks/useAdminWorkspaces';
import {
  WORKSPACES_COPY as WORDS,
  WORKSPACES_TABS,
  type VisibilityFilter,
  type WorkspaceFilter,
  type WorkspaceSortParam,
  type WorkspacesTab,
} from '../workspacesAdmin';
import { ConsoleAnswer, ConsoleHeader } from './ConsoleHeader';
import { OrganizationAccessLog } from './OrganizationAccessLog';
import { WorkspaceSheet } from './WorkspaceSheet';
import { WorkspacesTable } from './WorkspacesTable';

export interface WorkspacesView {
  tab: WorkspacesTab;
  q: string;
  show: WorkspaceFilter;
  sort: WorkspaceSortParam | undefined;
  visibility: VisibilityFilter;
  /** The workspace whose sheet is open. */
  workspace: string | undefined;
  /** The activity's range; absent is the last 30 days. */
  from: string | undefined;
  to: string | undefined;
  /** The one workspace the activity is narrowed to. */
  logWorkspace: string | undefined;
}

export type WorkspacesViewChange = (
  next: Partial<WorkspacesView>,
  how?: { push?: boolean },
) => void;

const PANEL_ID = 'admin-workspaces-panel';

/**
 * Admin → Workspaces (spec 191, service spec 157): how many workspaces there are
 * and how many have no active owner, then two tabs (spec 195): every workspace,
 * and what has happened to their access. They were one page with two identical
 * "Download CSV" buttons; each tab now carries its own export, named for what it
 * exports.
 *
 * A row opens a side sheet inside the console — who owns it, who is in it, its
 * recent access — so an admin answers "who owns X" without leaving. Changes stay
 * in the workspace's Settings, where they are built and logged; the sheet links
 * there.
 */
export function AdminWorkspacesPage({
  view,
  onChange,
}: {
  view: WorkspacesView;
  onChange: WorkspacesViewChange;
}) {
  const router = useRouter();
  const workspaces = useAdminWorkspaces();
  const data = workspaces.data;
  const open = data?.items.find((item) => item.id === view.workspace);

  /*
    Whether the open sheet was pushed by this page (a row, an activity entry), so
    closing it can step back to where it was opened from. Closing by replacing
    left the same list twice in the history, and the next Back seemed to do
    nothing. A sheet opened by its address (a shared link) has nothing of ours
    behind it, so closing that one replaces.
  */
  const pushedSheet = useRef(false);
  useEffect(() => {
    if (view.workspace === undefined) pushedSheet.current = false;
  }, [view.workspace]);
  const change: WorkspacesViewChange = (next, how) => {
    if (next.workspace !== undefined && how?.push) pushedSheet.current = true;
    onChange(next, how);
  };

  return (
    <section className={ADMIN_PAGE_SECTION}>
      <ConsoleHeader
        title={WORDS.title}
        description={WORDS.description}
        answer={
          data ? (
            <ConsoleAnswer
              figure={String(data.total)}
              words={WORDS.figureWords(data.total)}
              detail={
                data.without_active_owner > 0 ? (
                  <button
                    type="button"
                    onClick={() =>
                      // The slice's own order and every visibility, as the
                      // slice chip gives: a stale filter could open it empty.
                      onChange(
                        {
                          tab: 'workspaces',
                          show: 'without_owner',
                          sort: undefined,
                          visibility: 'any',
                        },
                        { push: view.tab !== 'workspaces' },
                      )
                    }
                    className="text-board-state-waiting focus-visible:ring-ring rounded-sm underline-offset-2 hover:underline focus-visible:ring-3 focus-visible:outline-none"
                  >
                    {WORDS.withoutOwner(data.without_active_owner)}
                  </button>
                ) : (
                  WORDS.allOwned
                )
              }
            />
          ) : undefined
        }
      />

      <div className="border-border border-b">
        <TabsList
          aria-label={WORDS.tabsLabel}
          className="max-md:w-full max-md:gap-0"
        >
          {WORKSPACES_TABS.map((value) => (
            <TabsTrigger
              key={value}
              id={`admin-workspaces-tab-${value}`}
              className="max-md:flex-1"
              selected={view.tab === value}
              panelId={PANEL_ID}
              onClick={() => {
                if (value !== view.tab)
                  onChange({ tab: value }, { push: true });
              }}
            >
              {WORDS.tabs[value]}
            </TabsTrigger>
          ))}
        </TabsList>
      </div>

      <div
        id={PANEL_ID}
        role="tabpanel"
        aria-labelledby={`admin-workspaces-tab-${view.tab}`}
        className="flex min-w-0 flex-col gap-3"
      >
        {view.tab === 'workspaces' ? (
          <WorkspacesTable
            workspaces={workspaces}
            view={view}
            onChange={change}
          />
        ) : (
          <OrganizationAccessLog
            view={view}
            onChange={change}
            workspaces={data?.items}
          />
        )}
      </div>

      <WorkspaceSheet
        workspace={open}
        onClose={() => {
          if (pushedSheet.current) {
            pushedSheet.current = false;
            router.history.back();
          } else {
            // Replaces: Back from here should not reopen what was just closed.
            onChange({ workspace: undefined });
          }
        }}
        onSeeAll={(id) => {
          pushedSheet.current = false;
          onChange(
            { tab: 'activity', logWorkspace: id, workspace: undefined },
            { push: true },
          );
        }}
      />
    </section>
  );
}
